"use client";

import { cn } from "@/lib/utils";
import { ReactNode, useState } from "react";

const BentoGrid = ({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={cn(
                "grid w-full auto-rows-[19rem] grid-cols-3 gap-5",
                className,
            )}
        >
            {children}
        </div>
    );
};

const BentoCard = ({
    name,
    className,
    background,
    Icon,
    description,
    href,
    cta,
    backContent,
    goBackLabel,
}: {
    name: string;
    className: string;
    background: ReactNode;
    Icon: any;
    description: string;
    href: string;
    cta: string;
    backContent?: ReactNode;
    goBackLabel?: string;
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!backContent) {
        return (
            <div
                key={name}
                className={cn(
                    "group relative col-span-3 flex flex-col justify-end overflow-hidden rounded-2xl",
                    "bg-gradient-to-b from-white to-gray-50/80",
                    "border border-gray-200/80",
                    "shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_rgba(124,58,237,0.04)]",
                    "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06),0_16px_40px_rgba(124,58,237,0.1)]",
                    "hover:border-purple-300/60",
                    "hover:-translate-y-1",
                    "transition-all duration-500 ease-out",
                    className,
                )}
            >
                <div>{background}</div>
                <div className="pointer-events-none z-10 flex flex-col gap-1.5 p-6 transition-all duration-500 group-hover:-translate-y-8">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 border border-purple-200/60 flex items-center justify-center mb-1 shadow-sm transition-all duration-500 group-hover:shadow-md group-hover:shadow-purple-200/40 group-hover:scale-90">
                        <Icon className="h-5 w-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 tracking-tight">{name}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
                </div>
                {/* Hover glow */}
                <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-gradient-to-t from-purple-500/[0.03] via-transparent to-transparent" />
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-60" />
            </div>
        );
    }

    // Expandable card with hover overlay
    return (
        <div
            key={name}
            className={cn(
                "group/card relative col-span-3 flex flex-col justify-end overflow-hidden rounded-2xl",
                "bg-gradient-to-b from-white to-gray-50/80",
                "border border-purple-200/70",
                "shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_rgba(124,58,237,0.06)]",
                "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06),0_16px_40px_rgba(124,58,237,0.12)]",
                "hover:border-purple-400/60",
                "hover:-translate-y-1",
                "transition-all duration-500 ease-out",
                className,
            )}
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
        >
            {/* Front content */}
            <div>{background}</div>
            <div className="pointer-events-none z-10 flex flex-col gap-1.5 p-6 transition-all duration-500">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 border border-purple-200/60 flex items-center justify-center mb-1 shadow-sm">
                    <Icon className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 tracking-tight">{name}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
            </div>

            {/* Hover glow */}
            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover/card:opacity-100 bg-gradient-to-t from-purple-500/[0.03] via-transparent to-transparent" />
            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-0 transition-opacity duration-500 group-hover/card:opacity-60" />

            {/* Slide-up overlay on hover */}
            <div
                className={cn(
                    "absolute inset-0 z-20 flex flex-col rounded-2xl transition-all duration-500 ease-out",
                    "bg-gradient-to-br from-white via-purple-50/40 to-white",
                    "border border-purple-200/60",
                    "shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]",
                    "backdrop-blur-sm",
                    expanded
                        ? "translate-y-0 opacity-100"
                        : "translate-y-full opacity-0 pointer-events-none"
                )}
            >
                <div className="flex-1 p-6 flex flex-col overflow-y-auto">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 border border-purple-200/60 flex items-center justify-center shrink-0 shadow-sm">
                            <Icon className="h-5 w-5 text-purple-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 tracking-tight">{name}</h3>
                    </div>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-purple-400 to-purple-200 rounded-full mb-3" />
                    <div className="flex-1 text-sm text-gray-600 leading-relaxed">
                        {backContent}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { BentoCard, BentoGrid };
